/**
 * The «Metin» tab: ordinary writing, with the few marks an e-mail really uses.
 *
 * WHY A RICH EDITOR AND NOT A TEXTAREA
 * ------------------------------------
 * A bare textarea asked the author for a SECOND body — plain text to sit beside the HTML — and then
 * asked them which of the two to send. One message, two inputs: this tab and the HTML tab edit the
 * same source, and what is typed here is HTML from the first keystroke, so nothing has to be kept
 * in step by hand and nothing has to be chosen.
 *
 * WHY `contentEditable` AND NOT A FRAMEWORK
 * -----------------------------------------
 * The marks needed are bold, italic, underline, a list and a link. That is what the platform's own
 * editing host does, in every browser, with no dependency and no design system of its own — and the
 * brief asks for a small, safe solution rather than a design tool. The element is UNCONTROLLED on
 * purpose: re-writing its markup from React on every keystroke would move the caret to the start of
 * the document, which is the classic way a rich editor becomes unusable.
 *
 * WHAT MAY BE PUT INTO IT, AND WHY THAT MATTERS
 * ---------------------------------------------
 * An editing host is part of the console's own page, so whatever is written into it runs there. It
 * is therefore only ever given markup the SERVER has already cleaned — the panel refuses to mount
 * this tab over an author's raw HTML until it has been through that call — and a paste is inserted
 * as plain text, so nothing can arrive through the clipboard either. What the host itself produces
 * from clean markup stays clean: the browser writes the tags, not the author.
 *
 * Whatever this produces is still sent to the server and cleaned again by the same call the send
 * path uses, because the message leaving the building is checked on the way out as well.
 */
import { type ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { CERTIFICATE_LINK, serverCleanedEmailBody } from '../../lib/email-html';

export interface RichEmailEditorProps {
  /**
   * The whole message, as the SERVER's cleaning call returned it.
   *
   * Not «the current source»: the panel mounts this tab only when what it holds came back from
   * that call, and hands it in under that name so the one place this component writes markup into
   * the page is reading something the server produced.
   */
  source: string | null;
  onChange: (fragment: string) => void;
  disabled?: boolean;
}

interface Mark {
  id: string;
  label: string;
  command: string;
  arg?: string;
}

const MARKS: Mark[] = [
  { id: 'bold', label: 'Kalın', command: 'bold' },
  { id: 'italic', label: 'İtalik', command: 'italic' },
  { id: 'underline', label: 'Altı çizili', command: 'underline' },
  { id: 'list', label: 'Madde listesi', command: 'insertUnorderedList' },
];

/**
 * An address this editor may put in an `href`, and the reason the list is this short.
 *
 * `createLink` writes whatever it is handed straight into the console's OWN page, so the address
 * box is a way into this document and not merely into the message: `javascript:…` typed there
 * becomes a live link on the screen the author is working on. The server would refuse it on the
 * way out — but «the next pass will catch it» is not a boundary, and the address is refused here,
 * where it is entered, with a reason the author can act on.
 */
function linkProblem(href: string): string | null {
  const value = href.trim();
  if (value === '') return 'Bir adres yazın.';
  if (value === CERTIFICATE_LINK) return null;
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return `Adres tam olmalı: https://… , mailto:… veya ${CERTIFICATE_LINK}.`;
  }
  if (url.protocol !== 'https:' && url.protocol !== 'mailto:')
    return 'Yalnız https:// ve mailto: adresleri eklenebilir.';
  return null;
}

export function RichEmailEditor({ source, onChange, disabled }: RichEmailEditorProps): ReactElement {
  const value = serverCleanedEmailBody(source);
  const host = useRef<HTMLDivElement>(null);
  const lastPushed = useRef<string>('');
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkHref, setLinkHref] = useState(CERTIFICATE_LINK);
  const [linkError, setLinkError] = useState<string | null>(null);

  /* Only write into the host when the value came from somewhere else — a load, a save, the HTML
     tab. Writing back what the host itself just reported would reset the caret on every letter. */
  useEffect(() => {
    const node = host.current;
    if (!node || value === lastPushed.current) return;
    lastPushed.current = value;
    node.innerHTML = value;
  }, [value]);

  const report = useCallback(() => {
    const node = host.current;
    if (!node) return;
    lastPushed.current = node.innerHTML;
    onChange(node.innerHTML);
  }, [onChange]);

  /** Text, put where the caret is, by the browser — the one way anything enters this host. */
  const insertPlain = useCallback(
    (text: string) => {
      const node = host.current;
      if (!node || disabled || text === '') return;
      node.focus();
      try {
        document.execCommand('insertText', false, text);
      } catch {
        /* a browser that refuses leaves the document unchanged, which the author can see */
      }
      report();
    },
    [disabled, report],
  );

  const apply = useCallback(
    (command: string, arg?: string) => {
      const node = host.current;
      if (!node || disabled) return;
      node.focus();
      /* `execCommand` is deprecated and still the only cross-browser editing host command set.
         A browser that refuses simply leaves the text as it was, which is a visible no-op. */
      try {
        document.execCommand(command, false, arg);
      } catch {
        /* nothing to recover: the document is unchanged and the author sees that it is */
      }
      report();
    },
    [disabled, report],
  );

  return (
    <div className="stack" style={{ gap: '6px' }}>
      <div className="row" style={{ gap: '4px', flexWrap: 'wrap' }} role="toolbar" aria-label="Biçimlendirme">
        {MARKS.map((mark) => (
          <button
            key={mark.id}
            type="button"
            data-testid={`email-mark-${mark.id}`}
            title={mark.label}
            disabled={disabled}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => apply(mark.command, mark.arg)}
          >
            {mark.label}
          </button>
        ))}
        <button
          type="button"
          data-testid="email-mark-link"
          disabled={disabled}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => setLinkOpen((open) => !open)}
        >
          Bağlantı
        </button>
      </div>

      {linkOpen ? (
        <div className="row" style={{ gap: '6px', flexWrap: 'wrap' }} data-testid="email-link-form">
          <input
            aria-label="Bağlantı adresi"
            data-testid="email-link-href"
            value={linkHref}
            onChange={(e) => setLinkHref(e.target.value)}
            style={{ flex: '1 1 240px' }}
          />
          <button
            type="button"
            data-testid="email-link-apply"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              const problem = linkProblem(linkHref);
              setLinkError(problem);
              if (problem !== null) return;
              apply('createLink', linkHref.trim());
              setLinkOpen(false);
            }}
          >
            Seçili metne uygula
          </button>
          {linkError === null ? null : (
            <p className="warn-box" style={{ margin: 0, flexBasis: '100%' }} data-testid="email-link-error">
              {linkError}
            </p>
          )}
        </div>
      ) : (
        <p className="muted" style={{ margin: 0, fontSize: '12px' }}>
          Sertifika bağlantısı için adres alanında <code>{CERTIFICATE_LINK}</code> bırakın; her alıcıya kendi
          adresi yerleştirilir.
        </p>
      )}

      {/*
        biome-ignore lint/a11y/useSemanticElements: a `<textarea>` holds text, not marks — this tab
        exists precisely so bold, a list and a link survive, which only an editing host does.
      */}
      <div
        ref={host}
        className="email-rich"
        data-testid="email-rich"
        tabIndex={0}
        role="textbox"
        aria-multiline="true"
        aria-label="Metin gövdesi"
        contentEditable={!disabled}
        suppressContentEditableWarning
        onInput={report}
        onBlur={report}
        onPaste={(e) => {
          /* Plain text only. The clipboard is the one way markup nobody checked could get in. */
          e.preventDefault();
          insertPlain(e.clipboardData.getData('text/plain'));
        }}
        onDrop={(e) => {
          /*
           * The OTHER way in, and it was open.
           *
           * Dragging a selection out of another page carries `text/html` with it, and the browser
           * drops that markup into an editing host as markup — past the paste handler, past the
           * panel's rule that this tab only ever holds what the server cleaned. A drop is read for
           * its text and nothing else, exactly like a paste.
           */
          e.preventDefault();
          insertPlain(e.dataTransfer.getData('text/plain'));
        }}
      />
    </div>
  );
}
